'use client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import SearchOrder from '@/components/ui/SearchInput/SearchOrder';
import SearchFilters from '@/components/ui/SearchInput/SearchFilters';
import GearChips from '@/components/ui/SearchInput/GearChips';
import ToneCard from '@/components/ui/ToneCard/ToneCard';
import Paginator from '@/components/ui/Paginator/Paginator';
import { useState, useEffect } from 'react';
import { ToneCountWithUser } from '@/utils/helpers';
import { createClient } from '@/utils/supabase/client';
import { searchTones } from '@/utils/supabase/queries';
import { SearchIcon } from 'lucide-react';
import SearchInput from '../SearchInput/SearchInput';
import LoadingDots from '../LoadingDots/LoadingDots';
import { ARCHITECTURE_VERSION, ArchitectureFilter, ArchitectureVersion, GEAR, MODEL_SIZE, FORMAT, GEAR_FORMAT_COMPATIBILITY } from '@/utils/constants';
import { analytics } from '@/lib/analytics';

const SearchClient = ({
  superArchitecture,
  superGears,
  superCalibrated,
  platform,
  tag,
  make,
  isApiSelect,
}: {
  superGears?: GEAR[];
  platform?: FORMAT;
  superArchitecture?: ArchitectureFilter;
  superCalibrated?: boolean;
  tag?: string;
  make?: string;
  isApiSelect?: boolean;
}) => {
  const searchParams = useSearchParams();
  const t = useTranslations('search');
  const selectPlatform = platform ?? undefined;
  // Architecture defaulting:
  //   - Select with explicit partner-arch → locked to that arch (radio locked).
  //   - Select without partner-arch (OAuth-no-arch) → no default; passes
  //     undefined to the RPC, which interprets NULL as the legacy A1+Custom
  //     default (excludes pure-A2 NAM tones).
  //   - Core → A2 (first-class default).
  // URL (`?architecture=…`) always wins over the implicit default.
  const flowLockedArchitecture: ArchitectureFilter | undefined = isApiSelect ? superArchitecture : undefined;
  const isLegacyPartner = isApiSelect && !superArchitecture;
  const defaultArchitecture: ArchitectureFilter | undefined = isLegacyPartner ? undefined : ARCHITECTURE_VERSION.A2;
  const urlArchitecture = searchParams.get('architecture') as ArchitectureVersion | null;
  const userArchitecture: ArchitectureFilter | undefined =
    (urlArchitecture && (urlArchitecture === ARCHITECTURE_VERSION.A1 || urlArchitecture === ARCHITECTURE_VERSION.A2 || urlArchitecture === ARCHITECTURE_VERSION.CUSTOM))
      ? urlArchitecture
      : undefined;
  const selectArchitecture: ArchitectureFilter | undefined =
    flowLockedArchitecture ?? userArchitecture ?? defaultArchitecture;
  const supabase = createClient();
  const pageSize = 10;
  const [tones, setTones] = useState<ToneCountWithUser[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const searchTerm = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const order = searchParams.get('order') || (searchTerm ? 'best-match' : 'trending');
  // const order = searchParams.get('order') || (searchTerm ? 'best-match' : 'newest');
  const tags = tag ? [tag] : searchParams.get('tags')?.split('_') || null;
  const makes = make ? [make] : searchParams.get('makes')?.split('_') || null;
  const gears = superGears ?? (searchParams.get('gears')?.split('_') as GEAR[]) ?? null;
  const creators = searchParams.get('creators')?.split('-') || null;
  // Partner-locked (OAuth flow) calibrated filter forces the value on; otherwise
  // it follows the URL toggle.
  const lockCalibrated = !!superCalibrated;
  const calibrated = lockCalibrated || searchParams.get('calibrated') === 'true';
  const verified = searchParams.get('verified') === 'true';
  const sizes = (searchParams.get('sizes')?.split(/[_-]/) as MODEL_SIZE[]) || null;
  // Format filter: `?format=nam|ir` from the sidebar drawer. An OAuth /
  // embed-locked Format (selectPlatform) always wins over the URL value.
  const urlFormatRaw = searchParams.get('format');
  const urlFormat =
    urlFormatRaw && Object.values(FORMAT).includes(urlFormatRaw as FORMAT)
      ? (urlFormatRaw as FORMAT)
      : undefined;
  const effectiveFormat: FORMAT | undefined = selectPlatform ?? urlFormat;
  // Hide the Format filter on gear-locked surfaces (category pages, gear-locked
  // select flows) whose gear supports only one format — e.g. Spaces are IR-only,
  // so there's nothing to choose. URL-driven gears on /search are
  // intentionally excluded: the gear is user-changeable there.
  const lockedGears = superGears;
  const formatFilterHidden =
    !!lockedGears?.length &&
    new Set(lockedGears.flatMap((g) => GEAR_FORMAT_COMPATIBILITY[g])).size <= 1;
  const [loading, setLoading] = useState<boolean>(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState<boolean>(false);
  // User-applied filters only — excludes pre-set gears (OAuth / legacy select),
  // pre-set tag/make (category pages), and select-flow platform/architecture.
  // Drives both the result count visibility and the filter-button dot indicator.
  // Gear-chip selections deliberately don't count — chips are their own UI surface,
  // distinct from the filter panel's dot+reset.
  const hasUserAppliedFilters =
    (!tag && !!tags?.length) ||
    (!make && !!makes?.length) ||
    !!creators?.length ||
    (!lockCalibrated && calibrated) ||
    verified ||
    !!sizes?.length ||
    !!urlFormat ||
    (!flowLockedArchitecture && selectArchitecture !== defaultArchitecture);
  // Technical filter is hidden when the effective Format is IR — IR has no
  // NAM-specific technical attributes (architecture, size).
  const technicalDisabled = effectiveFormat === FORMAT.IR;
  const hasUserFilters = !!searchTerm || hasUserAppliedFilters;

  const effectDeps = [
    page?.toString(),
    order?.toString(),
    searchTerm?.toString(),
    tags?.toString(),
    makes?.toString(),
    gears?.toString(),
    calibrated?.toString(),
    verified?.toString(),
    sizes?.toString(),
    creators?.toString(),
    effectiveFormat?.toString(),
    selectArchitecture?.toString(),
  ];

  useEffect(() => {
    const searchAsync = async () => {
      setLoading(true);
      const { tones, totalPages, totalCount } = await searchTones(
        supabase,
        {
          query: searchTerm,
          page,
          pageSize: 25,
          order,
          tags,
          makes,
          gears,
          calibrated,
          sizes,
          creators,
          platform: effectiveFormat,
          architecture: selectArchitecture,
          verifiedOnly: verified,
        },
        searchParams.get('rpc') ?? undefined
      );
      setTones(tones);
      setTotalCount(totalCount);
      setTotalPages(totalPages);
      setLoading(false);
    };
    searchAsync();
    analytics.capture('tone.searched', {
      has_query: !!searchTerm,
      order,
      has_tags_filter: !!tags,
      has_makes_filter: !!makes,
      has_gears_filter: !!gears,
      has_calibrated_filter: !!calibrated,
      has_sizes_filter: !!sizes,
      has_creators_filter: !!creators,
      has_verified_filter: verified,
      page,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, effectDeps);

  const countText =
    loading || !hasUserFilters ? '' : t('resultCount', { count: totalCount });

  return (
    <div className="max-w-6xl px-4 pt-6 mx-auto sm:pt-12 sm:px-6 flex flex-col gap-6 md:gap-8 min-h-screen pb-16">
      <div className="flex flex-col md:hidden">
        <SearchInput isApiSelect={isApiSelect} />
      </div>
      <div className="flex flex-col gap-6 md:gap-8">
        <GearChips
          superPlatform={effectiveFormat}
          selectGears={(isApiSelect ? superGears : undefined) ?? undefined}
          hideGearFilter={!isApiSelect && !!superGears?.length}
          hasUserAppliedFilters={!!hasUserAppliedFilters}
          onOpenMobileFilters={() => setMobileFiltersOpen(true)}
        />
        <div className="flex flex-row justify-between items-center gap-4 md:gap-12">
          {/* Desktop heading */}
          <h3 className="hidden md:block text-2xl font-bold truncate min-w-0 max-w-[520px] flex-1 md:max-w-[300px]">
            {t('refineYourSearch')}
          </h3>
          {/* Mobile count (heading removed). While refetching with filters
              applied, show LoadingDots in the same slot so the SearchOrder
              dropdown's width doesn't shift. */}
          {loading && hasUserFilters ? (
            <p className="md:hidden text-base font-semibold text-zinc-400 whitespace-nowrap min-w-[80px]">
              <LoadingDots />
            </p>
          ) : countText ? (
            <p className="md:hidden text-base font-semibold text-zinc-400 whitespace-nowrap">
              {countText}
            </p>
          ) : null}
          <div className="hidden md:flex flex-row gap-4 flex-1 justify-between items-center">
            <p className="text-base font-semibold text-zinc-400 whitespace-nowrap">
              {countText}
            </p>
            <SearchOrder />
          </div>
          <div className="md:hidden flex-1 min-w-0">
            <SearchOrder />
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:gap-12">
          <SearchFilters
            superPlatform={effectiveFormat}
            superTag={tag}
            superMake={make}
            selectArchitecture={selectArchitecture}
            flowLockedArchitecture={flowLockedArchitecture}
            defaultArchitecture={defaultArchitecture}
            lockCalibrated={lockCalibrated}
            technicalDisabled={technicalDisabled}
            formatLocked={!!selectPlatform}
            formatFilterHidden={formatFilterHidden}
            selectedGears={gears ?? undefined}
            mobileOpen={mobileFiltersOpen}
            onMobileOpenChange={setMobileFiltersOpen}
            hasUserAppliedFilters={!!hasUserAppliedFilters}
          />
          <div className="flex flex-col gap-8 flex-1 relative min-w-[0px]">
            {tones?.map((tone) => (
              <ToneCard
                key={tone.id}
                tone={tone}
                from="search_page"
                isApiSelect={isApiSelect}
                // The card's model-count rule follows the partner contract
                // (locked arch from the OAuth/embed flow), not the user's
                // interactive technical-filter selection. On core this is
                // always undefined → ToneCard falls through to the
                // `preferredModelsCount` (A2-or-em-dash) rule.
                architecture={flowLockedArchitecture ?? null}
              />
            ))}
            {loading && (
              <div className="flex flex-1 pt-24 justify-center items-start backdrop-blur-sm absolute inset-0 z-20">
                <LoadingDots />
              </div>
            )}
            {!tones?.length && !loading && (
              <div className="flex items-center justify-center max-w-[420px]">
                <div className="flex flex-col max-w-[320px] items-center justify-center px-8 py-8 mt-8 text-center bg-zinc-900 rounded-xl">
                  <div className="mb-4">
                    <SearchIcon className="flex-shrink-0 text-zinc-400" size={40} />
                  </div>
                  <h2 className="text-lg font-semibold text-white mb-2">{t('noResultsTitle')}</h2>
                  <p className="text-zinc-400">{t('noResultsBody')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Paginator totalPages={totalPages} page={page} totalCount={totalCount} pageSize={pageSize} />
    </div>
  );
};

export default SearchClient;
